import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AuthCallback from './pages/AuthCallback';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import QuizPage from './pages/QuizPage';
import CreditsPage from './pages/CreditsPage';
import RequireAuth from './components/RequireAuth';
import { Box, CircularProgress, Typography } from '@mui/material';
import AcceptInvitePage from './pages/AcceptInvitePage';
import TeamPage from './pages/TeamPage';
import AccountPage from './pages/AccountPage';
import SetupAccountPage from './pages/SetupAccountPage';
import { useAuth } from './context/AuthContext';
import AccountSetup from './components/AccountSetup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EmailVerification from './components/EmailVerification';
import { setupSessionTimeout, refreshSession, clearSessionTimeout } from './utils/sessionHandler';
import Navbar from './components/Navbar';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import GroupsPage from './pages/GroupPage';
import ProtectedRoute from './components/ProtectedRoute';
import InvitedUserSetup from './components/InvitedUserSetup';
import { TeamProvider } from './contexts/TeamContext';
import QuizResultsPage from './pages/QuizResultsPage';


// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="error" variant="h6">
            Something went wrong.
          </Typography>
          <pre>{this.state.error?.message}</pre>
        </Box>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const { loading, logout } = useAuth();
  console.log('AppContent rendering, loading:', loading);

  useEffect(() => {
    // Set up initial session timeout
    setupSessionTimeout(logout);

    // Add activity listeners
    const handleActivity = () => refreshSession(logout);
    
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keypress', handleActivity);
    document.addEventListener('click', handleActivity);
    document.addEventListener('scroll', handleActivity);

    return () => {
      // Clean up
      clearSessionTimeout();
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keypress', handleActivity);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('scroll', handleActivity);
    };
  }, [logout]);

  try {
    if (loading) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh' 
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <TeamProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Navbar />
          <Box sx={{ flex: 1, py: 3 }}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
              <Route path="/setup-account/:token" element={<SetupAccountPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              {/* Protected routes */}
              <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
              <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
              <Route path="/quiz" element={<RequireAuth><QuizPage /></RequireAuth>} />
              <Route path="/quiz/results/:resultId" element={<RequireAuth><QuizResultsPage /></RequireAuth>} />
              <Route path="/credits" element={<RequireAuth><CreditsPage /></RequireAuth>} />
              <Route path="/team" element={<RequireAuth><TeamPage /></RequireAuth>} />
              <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
              <Route path="/account-setup" element={<AccountSetup />} />
              <Route path="/verify-email/:token" element={<EmailVerification />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Add new payment result routes */}
              <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
              <Route path="/payment/cancel" element={<RequireAuth><PaymentCancel /></RequireAuth>} />

              {/* Add groups route */}
              <Route path="/groups" element={
                  <ProtectedRoute>
                      <GroupsPage />
                  </ProtectedRoute>
              } />

              {/* Add invited user setup route */}
              <Route path="/setup-account" element={<InvitedUserSetup />} />
              <Route path="/complete-setup" element={<AccountSetup />} />

            </Routes>
          </Box>
        </Box>
      </TeamProvider>
    );
  } catch (error) {
    console.error('Error in AppContent render:', error);
    return <div>Error loading application. Check console for details.</div>;
  }
}

function App() {
  console.log('App component rendering');
  return <AppContent />;
}

export default App;